<template>
  <div class="warehouse-form">
    <div class="card" style="max-width: 960px;">
      <form class="form-grid" @submit.prevent="saveItem">
        <div class="card-header">
          <div>
            <h2 class="title" style="font-size: 22px;">{{ isEdit ? 'Editar ferramenta' : 'Adicionar ferramenta' }}</h2>
            <p class="subtitle">Cadastro de itens no almoxarifado.</p>
          </div>
          <div class="actions">
            <button class="btn btn-ghost" type="button" @click="openXmlModal">Acrescentar nota em .xml</button>
            <button class="btn btn-ghost" type="button" @click="openDanfeModal">Escanear DANFE</button>
          </div>
        </div>

        <div class="grid" style="grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 14px;">
          <div class="input-field">
            <label for="item-code">ID unico</label>
            <input id="item-code" v-model="itemForm.code" type="text" />
          </div>

          <div class="input-field">
            <label for="item-name">Nome / descricao</label>
            <input id="item-name" v-model="itemForm.name" type="text" />
          </div>

          <div class="input-field">
            <label for="item-category">Categoria</label>
            <select id="item-category" v-model="itemForm.category">
              <option value="ferramenta">Ferramenta</option>
              <option value="consumivel">Consumivel</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          <div class="input-field">
            <label for="item-quantity-total">Quantidade total</label>
            <input id="item-quantity-total" v-model.number="itemForm.quantityTotal" type="number" min="0" />
          </div>

          <div class="input-field">
            <label for="item-quantity-min">Quantidade minima</label>
            <input id="item-quantity-min" v-model.number="itemForm.quantityMin" type="number" min="0" />
          </div>

          <div class="input-field">
            <label for="item-location">Area (projeto em andamento)</label>
            <select id="item-location" v-model="itemForm.location">
              <option value="">Sem area</option>
              <option v-for="project in projects" :key="project.id" :value="project.id">
                {{ project.name }}
              </option>
              <option v-if="hasLegacyLocation" :value="itemForm.location">
                {{ itemForm.location }}
              </option>
            </select>
          </div>

          <div class="input-field">
            <label for="item-condition">Estado</label>
            <input id="item-condition" v-model="itemForm.conditionStatus" type="text" />
          </div>

          <div class="input-field">
            <label for="item-unit-value">Valor unitario</label>
            <input id="item-unit-value" v-model.number="itemForm.unitValue" type="number" min="0" step="0.01" />
          </div>
        </div>

        <div class="input-field">
          <label>Imagem do item</label>
          <div
            class="image-grid"
            :class="{ 'is-drag-over': isDragOver }"
            @dragenter.prevent="isDragOver = true"
            @dragover.prevent="isDragOver = true"
            @dragleave.prevent="isDragOver = false"
            @drop.prevent="onImageDropped"
          >
            <div v-if="itemForm.photoDataUrl" class="image-preview">
              <img :src="$resolveUrl(itemForm.photoDataUrl)" alt="preview item" />
              <button class="image-remove" type="button" @click="removePhoto" aria-label="Remover foto">x</button>
            </div>
            <button v-else class="image-add" type="button" @click="triggerImageInput">+</button>
          </div>
          <input
            id="warehouse-item-photo"
            class="hidden-input"
            type="file"
            accept="image/*"
            @change="onPhotoSelected"
          />
        </div>

        <p v-if="error" class="subtitle" style="color: #a54b2a;">{{ error }}</p>

        <div class="actions">
          <button class="btn btn-primary" type="submit" :disabled="saving">
            {{ saving ? 'Salvando...' : (isEdit ? 'Salvar edicao' : 'Adicionar ferramenta') }}
          </button>
          <button class="btn btn-secondary" type="button" :disabled="saving" @click="resetItemForm">Limpar</button>
          <button class="btn btn-ghost" type="button" :disabled="saving" @click="goBack">Cancelar</button>
        </div>
      </form>
    </div>

    <div v-if="xmlModalOpen" class="xml-overlay">
      <div class="xml-card">
        <h3>Acrescentar nota em .xml</h3>
        <p class="subtitle">Envie o arquivo XML da nota para extrair os itens.</p>
        <div
          class="xml-dropzone"
          :class="{ 'is-drag-over': isXmlDragOver }"
          @dragenter.prevent="isXmlDragOver = true"
          @dragover.prevent="isXmlDragOver = true"
          @dragleave.prevent="isXmlDragOver = false"
          @drop.prevent="onXmlDropped"
        >
          <button class="btn btn-ghost" type="button" @click="triggerXmlInput">Selecionar arquivo .xml</button>
          <span class="item-meta">{{ xmlFileName || 'Nenhum arquivo selecionado' }}</span>
        </div>
        <input id="xml-note-input" class="hidden-input" type="file" accept=".xml,text/xml,application/xml" @change="onXmlSelected" />
        <div class="actions">
          <button class="btn btn-primary" type="button" :disabled="xmlLoading" @click="parseXmlFile">
            {{ xmlLoading ? 'Lendo arquivo...' : 'Ler XML' }}
          </button>
          <button class="btn btn-ghost" type="button" @click="closeXmlModal">Cancelar</button>
        </div>
        <p v-if="xmlError" class="subtitle error-text">{{ xmlError }}</p>

        <div v-if="xmlItems.length > 0" class="xml-list">
          <h4>Itens encontrados no XML</h4>
          <div v-for="(item, index) in xmlItems" :key="`${item.description}-${index}`" class="xml-item">
            <div>
              <strong>{{ item.description }}</strong>
              <div class="item-meta">Quantidade: {{ item.quantity }}</div>
            </div>
          </div>
          <div class="actions">
            <button class="btn btn-primary" type="button" @click="saveXmlItemsToStock">
              Salvar
            </button>
            <button class="btn btn-ghost" type="button" @click="closeXmlModal">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="danfeModalOpen" class="xml-overlay">
      <div class="xml-card">
        <h3>Escanear DANFE</h3>
        <p class="subtitle">Informe a chave/codigo de barras para consultar a API MeuDanfe.</p>
        <div class="input-field">
          <input v-model="danfeBarcode" type="text" placeholder="Chave de acesso (44 digitos)" @keyup.enter="lookupDanfe" />
        </div>
        <div class="actions">
          <button class="btn btn-primary" type="button" :disabled="danfeLoading" @click="lookupDanfe">
            {{ danfeLoading ? 'Consultando...' : 'Solicitar' }}
          </button>
          <button class="btn btn-ghost" type="button" @click="closeDanfeModal">Cancelar</button>
        </div>
        <p v-if="danfeError" class="subtitle error-text">{{ danfeError }}</p>

        <div v-if="danfeItems.length > 0" class="xml-list">
          <h4>Itens encontrados na DANFE</h4>
          <div v-for="(item, index) in danfeItems" :key="`danfe-${item.description}-${index}`" class="xml-item">
            <div>
              <strong>{{ item.description }}</strong>
              <div class="item-meta">Quantidade: {{ item.quantity }}</div>
            </div>
          </div>
          <div class="actions">
            <button class="btn btn-primary" type="button" @click="saveDanfeItemsToStock">Salvar</button>
            <button class="btn btn-ghost" type="button" @click="closeDanfeModal">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { apiFetch } from '@/store/api'
import { compressImage } from '@/utils/imageCompress'
import { newId } from '@/utils/id'
import { warehouseStore } from '@/store/warehouse'
import { projectsStore } from '@/store/projects'

export default defineComponent({
  name: 'WarehouseAddItemForm',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const error = ref('')
    const saving = ref(false)
    const isDragOver = ref(false)
    const xmlModalOpen = ref(false)
    const xmlLoading = ref(false)
    const xmlError = ref('')
    const xmlFile = ref<File | null>(null)
    const xmlFileName = ref('')
    const isXmlDragOver = ref(false)
    const xmlItems = ref<Array<{ description: string; quantity: number }>>([])
    const danfeModalOpen = ref(false)
    const danfeBarcode = ref('')
    const danfeLoading = ref(false)
    const danfeError = ref('')
    const danfeItems = ref<Array<{ description: string; quantity: number }>>([])

    const itemForm = ref({
      id: '',
      code: '',
      name: '',
      category: 'ferramenta',
      quantityTotal: 0,
      quantityMin: 0,
      location: '',
      conditionStatus: 'bom',
      unitValue: 0,
      photoDataUrl: ''
    })

    const editId = computed(() => {
      const value = route.query.edit
      return typeof value === 'string' ? value : ''
    })
    const isEdit = computed(() => !!itemForm.value.id)
    const projects = computed(() => projectsStore.all())
    const hasLegacyLocation = computed(() => {
      if (!itemForm.value.location) return false
      return !projects.value.some((project) => project.id === itemForm.value.location)
    })

    const resetItemForm = () => {
      itemForm.value = {
        id: '',
        code: '',
        name: '',
        category: 'ferramenta',
        quantityTotal: 0,
        quantityMin: 0,
        location: '',
        conditionStatus: 'bom',
        unitValue: 0,
        photoDataUrl: ''
      }
    }

    const loadEditItem = () => {
      if (!editId.value) {
        resetItemForm()
        return
      }
      const item = warehouseStore.all().find((entry) => entry.id === editId.value)
      if (!item) {
        error.value = 'Item para edicao nao encontrado.'
        return
      }
      itemForm.value = {
        id: item.id,
        code: item.code,
        name: item.name,
        category: item.category || 'ferramenta',
        quantityTotal: item.quantityTotal,
        quantityMin: item.quantityMin,
        location: item.location,
        conditionStatus: item.conditionStatus || 'bom',
        unitValue: item.unitValue,
        photoDataUrl: item.photoDataUrl || ''
      }
    }

    const triggerImageInput = () => {
      const input = document.getElementById('warehouse-item-photo') as HTMLInputElement | null
      if (input) input.click()
    }

    const removePhoto = () => {
      itemForm.value.photoDataUrl = ''
    }

    const processPhotoFile = async (file: File) => {
      isDragOver.value = false
      if (!file.type.startsWith('image/')) {
        error.value = 'Selecione um arquivo de imagem valido.'
        return
      }
      try {
        // Item: máx 512×512, alvo 200KB
        itemForm.value.photoDataUrl = await compressImage(file, 512, 512)
        error.value = ''
      } catch {
        error.value = 'Falha ao processar imagem.'
      }
    }

    const onPhotoSelected = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      if (!file) return
      processPhotoFile(file)
      input.value = ''
    }

    const onImageDropped = (event: DragEvent) => {
      isDragOver.value = false
      const file = event.dataTransfer?.files?.[0]
      if (!file) return
      processPhotoFile(file)
    }

    const openXmlModal = () => {
      xmlModalOpen.value = true
      xmlError.value = ''
    }

    const closeXmlModal = () => {
      xmlModalOpen.value = false
      xmlLoading.value = false
      xmlError.value = ''
      xmlFile.value = null
      xmlFileName.value = ''
      isXmlDragOver.value = false
      xmlItems.value = []
    }

    const triggerXmlInput = () => {
      const input = document.getElementById('xml-note-input') as HTMLInputElement | null
      if (input) input.click()
    }

    const setXmlFile = (file: File | null) => {
      if (!file) return
      const isXmlType = file.name.toLowerCase().endsWith('.xml') || file.type.includes('xml')
      if (!isXmlType) {
        xmlError.value = 'Selecione um arquivo .xml valido.'
        return
      }
      xmlError.value = ''
      xmlFile.value = file
      xmlFileName.value = file.name
    }

    const onXmlSelected = (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0] || null
      setXmlFile(file)
      input.value = ''
    }

    const onXmlDropped = (event: DragEvent) => {
      isXmlDragOver.value = false
      const file = event.dataTransfer?.files?.[0] || null
      setXmlFile(file)
    }

    const readFileText = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result || ''))
        reader.onerror = () => reject(new Error('Falha ao ler arquivo XML.'))
        reader.readAsText(file, 'utf-8')
      })

    const textFromElement = (element: Element | null, tag: string) => {
      if (!element) return ''
      const node = element.getElementsByTagName(tag)[0]
      return (node?.textContent || '').trim()
    }

    const parseXmlFile = async () => {
      xmlError.value = ''
      xmlItems.value = []
      if (!xmlFile.value) {
        xmlError.value = 'Selecione um arquivo XML da nota.'
        return
      }
      xmlLoading.value = true
      try {
        const xmlText = await readFileText(xmlFile.value)
        const xmlDoc = new DOMParser().parseFromString(xmlText, 'application/xml')
        if (xmlDoc.getElementsByTagName('parsererror').length > 0) {
          xmlError.value = 'XML invalido. Verifique o arquivo enviado.'
          return
        }
        const prodNodes = Array.from(xmlDoc.getElementsByTagName('prod'))
        const parsed = prodNodes
          .map((prod) => {
            const description = textFromElement(prod, 'xProd')
            const qtyTrib = textFromElement(prod, 'qTrib') || textFromElement(prod, 'qCom')
            const quantity = Math.max(0, Number(qtyTrib.replace(',', '.')))
            if (!description) return null
            return { description, quantity }
          })
          .filter((entry): entry is { description: string; quantity: number } => Boolean(entry))
        xmlItems.value = parsed
        if (xmlItems.value.length === 0) {
          xmlError.value = 'Nao foram encontrados itens <prod> com xProd e qTrib no XML.'
        }
      } catch (err: unknown) {
        xmlError.value = err instanceof Error ? err.message : 'Falha ao processar XML.'
      } finally {
        xmlLoading.value = false
      }
    }

    const generateXmlCode = (description: string, index: number) => {
      const base = description
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
        .slice(0, 8) || 'ITEM'
      const stamp = newId().slice(0, 6).toUpperCase()
      return `XML-${base}-${index + 1}-${stamp}`
    }

    const saveXmlItemsToStock = async () => {
      if (xmlItems.value.length === 0) {
        xmlError.value = 'Nao ha itens do XML para salvar.'
        return
      }
      for (let index = 0; index < xmlItems.value.length; index += 1) {
        const item = xmlItems.value[index]
        await warehouseStore.upsert({
          code: generateXmlCode(item.description, index),
          name: item.description.trim(),
          category: 'consumivel',
          quantityTotal: Math.max(0, Number(item.quantity || 0)),
          quantityMin: 0,
          location: itemForm.value.location.trim(),
          conditionStatus: 'novo',
          unitValue: 0,
          photoDataUrl: ''
        })
      }
      closeXmlModal()
      await warehouseStore.refresh()
    }

    const openDanfeModal = () => {
      danfeModalOpen.value = true
      danfeError.value = ''
      danfeBarcode.value = ''
      danfeItems.value = []
    }

    const closeDanfeModal = () => {
      danfeModalOpen.value = false
      danfeLoading.value = false
      danfeError.value = ''
      danfeBarcode.value = ''
      danfeItems.value = []
    }

    const lookupDanfe = async () => {
      danfeError.value = ''
      danfeItems.value = []
      if (!danfeBarcode.value.trim()) {
        danfeError.value = 'Informe a chave/codigo de barras da DANFE.'
        return
      }
      danfeLoading.value = true
      try {
        const response = await apiFetch<{ items: Array<{ description: string; quantity: number }> }>('/warehouse/danfe/lookup', {
          method: 'POST',
          body: JSON.stringify({ barcode: danfeBarcode.value.trim() })
        })
        danfeItems.value = Array.isArray(response.items) ? response.items : []
        if (danfeItems.value.length === 0) {
          danfeError.value = 'A DANFE foi consultada, mas nao retornou itens.'
        }
      } catch (err: unknown) {
        danfeError.value = err instanceof Error ? err.message : 'Falha ao consultar DANFE.'
      } finally {
        danfeLoading.value = false
      }
    }

    const saveDanfeItemsToStock = async () => {
      if (danfeItems.value.length === 0) {
        danfeError.value = 'Nao ha itens da DANFE para salvar.'
        return
      }
      for (let index = 0; index < danfeItems.value.length; index += 1) {
        const item = danfeItems.value[index]
        await warehouseStore.upsert({
          code: generateXmlCode(item.description, index),
          name: item.description.trim(),
          category: 'consumivel',
          quantityTotal: Math.max(0, Number(item.quantity || 0)),
          quantityMin: 0,
          location: itemForm.value.location.trim(),
          conditionStatus: 'novo',
          unitValue: 0,
          photoDataUrl: ''
        })
      }
      closeDanfeModal()
      await warehouseStore.refresh()
    }

    const saveItem = async () => {
      error.value = ''
      if (!itemForm.value.code.trim() || !itemForm.value.name.trim()) {
        error.value = 'Preencha ID unico e nome/descricao.'
        return
      }

      saving.value = true
      try {
        await warehouseStore.upsert({
          id: itemForm.value.id || undefined,
          code: itemForm.value.code.trim(),
          name: itemForm.value.name.trim(),
          category: itemForm.value.category.trim(),
          quantityTotal: itemForm.value.quantityTotal,
          quantityMin: itemForm.value.quantityMin,
          location: itemForm.value.location.trim(),
          conditionStatus: itemForm.value.conditionStatus.trim(),
          unitValue: itemForm.value.unitValue,
          photoDataUrl: itemForm.value.photoDataUrl
        })

        await router.push('/almoxarifado?tab=estoque')
      } finally {
        saving.value = false
      }
    }

    const goBack = () => {
      router.push('/almoxarifado?tab=estoque')
    }

    onMounted(async () => {
      await Promise.all([warehouseStore.refresh(), projectsStore.refresh()]).catch(() => {
        error.value = 'Nao foi possivel carregar itens do almoxarifado.'
      })
      loadEditItem()
    })

    watch(editId, () => {
      loadEditItem()
    })

    return {
      itemForm,
      isEdit,
      projects,
      hasLegacyLocation,
      error,
      saving,
      isDragOver,
      xmlModalOpen,
      xmlLoading,
      xmlError,
      xmlFileName,
      xmlItems,
      isXmlDragOver,
      danfeModalOpen,
      danfeBarcode,
      danfeLoading,
      danfeError,
      danfeItems,
      triggerImageInput,
      onPhotoSelected,
      onImageDropped,
      openXmlModal,
      closeXmlModal,
      triggerXmlInput,
      onXmlSelected,
      onXmlDropped,
      parseXmlFile,
      saveXmlItemsToStock,
      openDanfeModal,
      closeDanfeModal,
      lookupDanfe,
      saveDanfeItemsToStock,
      removePhoto,
      saveItem,
      resetItemForm,
      goBack
    }
  }
})
</script>

<style scoped lang="scss">
.warehouse-form {
  width: 100%;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
  gap: 10px;
  padding: 6px;
  border: 1px dashed transparent;
  border-radius: 12px;
}

.image-grid.is-drag-over {
  border-color: var(--system-navbar, #4f3300);
  background: color-mix(in srgb, var(--system-navbar-text, #f3ede3) 18%, transparent);
}

.image-preview {
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid rgba(31, 30, 26, 0.14);
  aspect-ratio: 1 / 1;
  width: 108px;
}

.image-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-remove {
  position: absolute;
  top: 6px;
  right: 6px;
  border: none;
  border-radius: 8px;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  cursor: pointer;
}

.image-add {
  width: 108px;
  height: 108px;
  border-radius: 12px;
  border: 1px dashed rgba(31, 30, 26, 0.2);
  display: grid;
  place-items: center;
  font-size: 32px;
  color: var(--accent, #0f6b6e);
  background: rgba(15, 107, 110, 0.08);
  cursor: pointer;
}

.xml-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: grid;
  place-items: center;
  z-index: 999;
  padding: 18px;
}

.xml-card {
  width: min(760px, 100%);
  max-height: 90vh;
  overflow-y: auto;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 18px 45px rgba(31, 30, 26, 0.22);
  display: grid;
  gap: 12px;
}

.xml-list {
  border-top: 1px solid rgba(31, 30, 26, 0.12);
  padding-top: 12px;
  display: grid;
  gap: 10px;
}

.xml-item {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: flex-start;
  border: 1px solid rgba(31, 30, 26, 0.12);
  border-radius: 12px;
  padding: 10px;
}

.xml-dropzone {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 10px;
  align-items: center;
  border: 1px solid rgba(31, 30, 26, 0.12);
  border-radius: 12px;
  padding: 10px;
}

.xml-dropzone.is-drag-over {
  border-color: var(--system-navbar, #4f3300);
  background: color-mix(in srgb, var(--system-navbar-text, #f3ede3) 18%, transparent);
}

.error-text {
  color: #a54b2a;
}
</style>
